// Pure helpers for the chat-style refine thread on the create page.
//
// The create page keeps a visible conversation (`thread`) of the user's prompts
// and short assistant acks. When the user follows on with a refine instruction,
// we need to (a) turn that visible thread into the compact `history` wire shape
// the generate endpoint expects, and (b) assemble the full GenerateRequest that
// carries the new prompt, the prior history, and the current model as context.
//
// Both helpers are deliberately PURE so they unit-test without a DOM, exactly
// like deck-outline.ts. British spelling throughout. No emojis.

import type { GenerateRequest, GenerateQuality } from '../api/types';
import type { DeckModel } from './deck-model';

/** One visible turn in the refine conversation. The `text` is shown verbatim
 *  in the UI (rendered as TEXT via Lit's default escaping — never unsafeHTML). */
export interface ThreadTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** The compact wire shape the generate endpoint accepts as conversation
 *  context. Mirrors GenerateRequest['history'] element type. */
export interface HistoryEntry {
  role: 'user' | 'assistant';
  content: string;
}

/** The persisted conversation shape stored on a Deck (`Deck.conversation`).
 *  Looser than ThreadTurn: `role` is a plain string so legacy/foreign data
 *  round-trips. Mirrors CreateDeckRequest['conversation'] element type. */
export interface ConversationEntry {
  role: string;
  text: string;
}

/**
 * Map the visible thread to the persisted `{ role, text }[]` conversation shape
 * stored on a Deck. The `text` carries over verbatim; `role` is widened to a
 * plain string. The result is a NEW array (the input is never mutated). Used by
 * the create page to persist the refine thread alongside an instant draft so a
 * refresh can rehydrate it.
 */
export function threadToConversation(
  thread: ReadonlyArray<ThreadTurn>,
): ConversationEntry[] {
  return thread.map((turn) => ({ role: turn.role, text: turn.text }));
}

/**
 * Inverse of {@link threadToConversation}: map a persisted conversation back to
 * the visible thread, narrowing the loose `role` string to the ThreadTurn union
 * (anything that is not exactly "assistant" becomes "user"). Used on resume to
 * rebuild the chat from `Deck.conversation`.
 */
export function conversationToThread(
  conversation: ReadonlyArray<ConversationEntry>,
): ThreadTurn[] {
  return conversation.map((c) => ({
    role: c.role === 'assistant' ? 'assistant' : 'user',
    text: c.text,
  }));
}

/**
 * Map the visible thread to the compact `{ role, content }[]` history the
 * generate endpoint expects. The `text` field becomes `content`; nothing else
 * carries over. The result is a NEW array (the input is never mutated).
 */
export function threadToHistory(thread: ReadonlyArray<ThreadTurn>): HistoryEntry[] {
  return thread.map((turn) => ({ role: turn.role, content: turn.text }));
}

/**
 * Build the GenerateRequest for a refine (follow-on) call. The new instruction
 * is the `prompt`; the prior conversation is mapped to `history`; the deck being
 * refined is sent as `currentModel`; `quality` carries the user's tier through.
 *
 * `history` and `currentModel` are only attached when meaningful (a non-empty
 * thread / a defined model) so the wire body stays minimal — matching the
 * client's "only when present" contract.
 */
export function buildRefineRequest(args: {
  prompt: string;
  thread: ReadonlyArray<ThreadTurn>;
  currentModel?: DeckModel;
  quality?: GenerateQuality;
}): GenerateRequest {
  const req: GenerateRequest = { prompt: args.prompt };
  const history = threadToHistory(args.thread);
  if (history.length > 0) req.history = history;
  if (args.currentModel) req.currentModel = args.currentModel;
  if (args.quality) req.quality = args.quality;
  return req;
}
